<template>
  <EditorShell
    ref="shell"
    :items="shellItems"
    item-label="token"
    @remove="onShellRemove"
  >
    <template #add-panel>
      <div v-if="tokensLoading" class="field-desc">Loading token skins…</div>
      <div v-else-if="tokensError" class="field-error">{{ tokensError }}</div>
      <SkinGrid
        v-else
        :skins="tokenSkins"
        :selection-mode="true"
        :selected-ids="selectedTokenIds"
        :show-info="false"
        :card-size="skinSize"
        @toggle="toggleToken"
      />
    </template>

    <template #edit-panel="{ item, idx, openLightbox }">
      <div class="ve-edit-form">
        <div class="ve-edit-row-head">
          <img
            v-if="item.thumbnailUrl"
            :src="item.thumbnailUrl"
            class="ve-edit-thumb"
            :style="{ width: skinSize + 'px', height: skinSize + 'px' }"
            @click="openLightbox(item.thumbnailUrl)"
          />
          <div>
            <div class="ve-edit-skin-name">{{ tokenSkinById(item.id).name }}</div>
            <div class="ve-edit-hint">Token {{ idx + 1 }} of {{ selectedTokenIds.length }}</div>
          </div>
        </div>
        <div class="ve-edit-group">
          <label class="ve-edit-label">Name</label>
          <input
            :value="(tokenDetails[item.id] || {}).name"
            type="text"
            class="field-input"
            @input="onTokenNameInput(item.id, $event.target.value)"
          />
        </div>
      </div>
    </template>
  </EditorShell>
</template>

<script>
import EditorShell from '@/components/EditorShell.vue';
import SkinGrid from '@/components/SkinGrid.vue';

export default {
  name: 'TokensEditor',
  components: { EditorShell, SkinGrid },

  props: {
    value: { type: Array, default: () => [] },
    skinSize: { type: Number, default: 64 },
  },

  data() {
    return {
      tokenSkins: [],
      tokensLoading: false,
      tokensError: '',
      selectedTokenIds: [],
      tokenDetails: {},
    };
  },

  computed: {
    shellItems() {
      return this.selectedTokenIds.map(skinId => {
        const skin = this.tokenSkinById(skinId);
        return {
          id: skinId,
          thumbnailUrl: skin.thumbnailUrl || '',
          name: (this.tokenDetails[skinId] || {}).name || skin.name || skinId,
        };
      });
    },
  },

  async mounted() {
    await this.fetchTokenSkins();
  },

  watch: {
    value(newVal) {
      if (this.tokenSkins.length > 0) {
        this.initFromValue(newVal);
      }
    },
  },

  methods: {
    async fetchTokenSkins() {
      this.tokensLoading = true;
      this.tokensError = '';
      try {
        const res = await fetch(
          '/GetCatalogueSkins?viewMode=public&search=token&perPage=200&page=1&sortOrder=newest',
          { credentials: 'include' }
        );
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        this.tokenSkins = data.result.skins;
        this.$emit('catalogue-loaded', this.tokenSkins);
        if (this.value && this.value.length > 0) {
          this.initFromValue(this.value);
        }
      } catch (err) {
        this.tokensError = 'Could not load token skins: ' + err.message;
      } finally {
        this.tokensLoading = false;
      }
    },

    initFromValue(tokens) {
      const matchedIds = [];
      for (const token of (tokens || [])) {
        let skin;
        if (token.SkinId != null) {
          skin = this.tokenSkins.find(s => String(s.id) === String(token.SkinId));
        }
        if (!skin) {
          skin = this.tokenSkins.find(s => s.name === token.Name);
        }
        if (skin && !matchedIds.includes(String(skin.id))) {
          matchedIds.push(String(skin.id));
          this.$set(this.tokenDetails, String(skin.id), { name: token.Name });
        }
      }
      this.selectedTokenIds = matchedIds;
    },

    tokenSkinById(skinId) {
      return this.tokenSkins.find(s => String(s.id) === skinId) || {};
    },

    toggleToken(skin) {
      const id = String(skin.id);
      if (this.selectedTokenIds.includes(id)) return;
      this.selectedTokenIds.push(id);
      this.$set(this.tokenDetails, id, { name: skin.name || '' });
      this.$refs.shell.selectIdx(this.selectedTokenIds.length - 1);
      this.$emit('input', this.buildTokensJson());
    },

    onShellRemove(item, idx) {
      this.selectedTokenIds.splice(idx, 1);
      this.$delete(this.tokenDetails, item.id);
      this.$emit('input', this.buildTokensJson());
    },

    onTokenNameInput(skinId, newName) {
      this.$set(this.tokenDetails, skinId, { ...this.tokenDetails[skinId], name: newName });
      this.$emit('input', this.buildTokensJson());
    },

    buildTokensJson() {
      return this.tokenSkins
        .filter(s => this.selectedTokenIds.includes(String(s.id)))
        .map((s, i) => ({
          Id: `token_${i}`,
          SkinId: s.id,
          Name: (this.tokenDetails[String(s.id)] || {}).name || s.name,
          Type: 'Token',
        }));
    },
  },
};
</script>


